import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSectionActive } from './SectionActive';

// Detail screens need to say more than which section they are in — "Files"
// is not a useful answer when you are three levels down looking at one
// driver's medical certificate. A page declares its own trail and the
// topbar renders it after the section name.

export interface Crumb { label: string; to?: string }

interface TrailApi {
  trail: Crumb[];
  setTrail: (trail: Crumb[]) => void;
}

const BreadcrumbContext = createContext<TrailApi>({ trail: [], setTrail: () => undefined });

export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [trail, setTrail] = useState<Crumb[]>([]);
  const value = useMemo(() => ({ trail, setTrail }), [trail]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

export const useBreadcrumbTrail = (): Crumb[] => useContext(BreadcrumbContext).trail;

/**
 * Declare this screen's trail. Pass a stable-enough array — it is compared
 * by value, so an inline literal is fine and will not loop.
 */
export function usePageBreadcrumb(trail: Crumb[]): void {
  const { setTrail } = useContext(BreadcrumbContext);
  const key = JSON.stringify(trail);
  // Only the page on screen owns the trail; a kept-alive one hands it
  // back when hidden and takes it again when shown.
  const active = useSectionActive();

  useEffect(() => {
    if (!active) return undefined;
    setTrail(JSON.parse(key) as Crumb[]);
    return () => setTrail([]);
  }, [key, setTrail, active]);
}
